import type { V2Area, V2Inspection } from './inspection';
import { inspectionServiceOf, type InspectionService } from './inspectionService';
import { normalizeJurisdictionCode } from './jurisdiction';

// New York report timing (RPL §444-g(3); NY-REPORT-AUTHORITY RP-R01): the client receives the
// written report within five business days after the inspection. Owner ruling OD-12 / approved
// draft 2026-09-09 §5.4: the app REMINDS, it never blocks generating or sharing; the five days
// count from the inspection being marked complete, or else from its last area being completed.
// Saturdays and Sundays are skipped; public holidays are not, so the day shown is never later
// than the legal one.
export const NY_REPORT_BUSINESS_DAYS = 5;

export function addBusinessDays(from: number, days: number): number {
  const d = new Date(from);
  let left = days;
  while (left > 0) {
    d.setDate(d.getDate() + 1);
    const weekday = d.getDay();
    if (weekday !== 0 && weekday !== 6) left -= 1;
  }
  return d.getTime();
}

// When the five days start counting, or null while the inspection is still in the field.
export function reportClockStart(inspection: Pick<V2Inspection, 'completedAt'>, areas: ReadonlyArray<Pick<V2Area, 'enabled' | 'completedAt'>>): number | null {
  if (inspection.completedAt !== null) return inspection.completedAt;
  const enabled = areas.filter((a) => a.enabled);
  if (enabled.length === 0) return null;
  let last = 0;
  for (const area of enabled) {
    if (area.completedAt === null) return null;
    last = Math.max(last, area.completedAt);
  }
  return last;
}

export interface ReportDue {
  dueAt: number;
  overdue: boolean;
  // Which promise the reminder rests on: the statute for a home inspection, the agreement's own
  // records-and-retention clause for a New Build observation (RPL §444-g(3) does not reach a
  // service that is not an Article 12-B home inspection — OD-9). The wording differs; the five
  // business days do not.
  service: InspectionService;
}

// null = nothing to remind: not a New York inspection, the final report already exists, or the
// clock has not started. Overdue once the whole due day has passed.
export function nyReportDue(
  inspection: Pick<V2Inspection, 'property' | 'finalizedAt' | 'completedAt'>,
  areas: ReadonlyArray<Pick<V2Area, 'enabled' | 'completedAt'>>,
  now: number
): ReportDue | null {
  if (normalizeJurisdictionCode(inspection.property.state) !== 'NY') return null;
  if (inspection.finalizedAt !== null) return null;
  const start = reportClockStart(inspection, areas);
  if (start === null) return null;
  const dueAt = addBusinessDays(start, NY_REPORT_BUSINESS_DAYS);
  const dueDayEnd = new Date(dueAt);
  dueDayEnd.setHours(23, 59, 59, 999);
  return { dueAt, overdue: now > dueDayEnd.getTime(), service: inspectionServiceOf(inspection.property) };
}
